import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, imgUrl, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, Notice } from "@/components/RequireAuth";
import { ReviewItem } from "@/components/Reviews";
import ListingCard from "@/components/ListingCard";
import StatusBadge from "@/components/StatusBadge";
import PhotoUploader from "@/components/PhotoUploader";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BadgeCheck, UserX } from "lucide-react";

function EditProfile({ onDone }) {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: user.name, bio: user.bio || "", picture: user.picture || "", is_host: !!user.is_host });
  const save = async () => {
    try {
      const { data } = await api.patch("/users/me", f);
      setUser(data);
      toast.success("Profile saved");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="profile-edit-button">Edit profile</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit profile</DialogTitle></DialogHeader>
        <div className="space-y-2"><Label>Photo</Label>
          <PhotoUploader value={f.picture ? [f.picture] : []} onChange={(v) => setF({ ...f, picture: v[v.length - 1] || "" })} kind="avatar" max={1} testid="profile-photo" />
        </div>
        <div className="space-y-2"><Label>Name</Label><Input data-testid="profile-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="space-y-2"><Label>About you</Label><Textarea data-testid="profile-bio-input" rows={4} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="Hosts see this when you request a stay" /></div>
        {user.is_host && <label className="flex items-center gap-3 text-sm font-medium"><Switch data-testid="profile-hosting-switch" checked={f.is_host} onCheckedChange={(v) => setF({ ...f, is_host: v })} />Hosting is on</label>}
        <Button onClick={save} className="rounded-full" data-testid="profile-save-button">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ v, l, testid }) => (
  <div className="rounded-2xl border bg-white p-4 text-center"><p className="font-mono-num text-2xl font-semibold" data-testid={testid}>{v}</p><p className="text-xs text-stone-500">{l}</p></div>
);

export default function Profile() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: p, isLoading, error } = useQuery({ queryKey: ["profile", id], queryFn: () => api.get(`/users/${id}/profile`).then((r) => r.data) });
  if (isLoading) return <Spinner />;
  if (error) return <Notice icon={UserX} title="User not found" testid="profile-not-found" />;
  const own = user?.user_id === id;
  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-[320px_1fr]">
        <aside className="space-y-4">
          <div className="rounded-3xl border bg-white p-8 text-center" data-testid="profile-card">
            <Avatar className="mx-auto h-28 w-28"><AvatarImage src={imgUrl(p.picture)} /><AvatarFallback className="text-3xl">{p.name?.[0]}</AvatarFallback></Avatar>
            <h1 className="mt-4 text-2xl font-bold" data-testid="profile-name">{p.name}</h1>
            <p className="mt-1 inline-flex items-center gap-1 text-sm text-primary" data-testid="profile-verified"><BadgeCheck className="h-4 w-4" />Email verified with Google</p>
            <p className="mt-1 text-xs text-stone-500">Member since {fmtDate(p.created_at)}{p.is_host ? " · Host" : ""}</p>
            {own && <div className="mt-5"><EditProfile onDone={() => qc.invalidateQueries({ queryKey: ["profile", id] })} /></div>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat v={p.past_stays} l="past stays" testid="profile-past-stays" />
            <Stat v={p.guest_rating ?? "–"} l="guest rating" testid="profile-guest-rating" />
            <Stat v={p.complaints ? p.complaints.length : "–"} l="complaints" testid="profile-complaint-count" />
          </div>
        </aside>
        <div className="space-y-10">
          <section>
            <h2 className="mb-3 text-xl font-semibold">About</h2>
            <p className="whitespace-pre-line text-stone-700" data-testid="profile-bio">{p.bio || (own ? "Add a short bio so hosts know who's coming." : "No bio yet.")}</p>
          </section>
          <section>
            <h2 className="mb-5 text-xl font-semibold">Reviews from hosts</h2>
            {p.guest_reviews.length ? <div className="grid gap-8 md:grid-cols-2" data-testid="profile-guest-reviews">{p.guest_reviews.map((r) => <ReviewItem key={r.review_id} r={r} />)}</div> : <p className="text-sm text-stone-500">No host reviews yet.</p>}
          </section>
          {p.complaints && <section>
            <h2 className="mb-4 text-xl font-semibold">Complaint history</h2>
            {p.complaints.length ? (
              <div className="space-y-2" data-testid="profile-complaints">
                {p.complaints.map((c) => <div key={c.complaint_id} className="flex items-center gap-3 rounded-2xl border bg-white px-4 py-3 text-sm"><span className="font-medium">{c.category}</span><span className="text-stone-500">{fmtDate(c.created_at)}</span><span className="ml-auto"><StatusBadge status={c.decision || c.status} /></span></div>)}
              </div>
            ) : <p className="text-sm text-stone-500">No complaints filed.</p>}
          </section>}
          {p.listings.length > 0 && (
            <section>
              <h2 className="mb-5 text-xl font-semibold">{p.name?.split(" ")[0]}'s places</h2>
              <div className="grid gap-6 sm:grid-cols-2">{p.listings.map((l) => <ListingCard key={l.listing_id} l={l} />)}</div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
